import Notifications from 'react-notification-system-redux';
import { api } from '../../../general/apiScenarios';
import { ListenerApiSafe, _select, _take } from '../../../general/listenerMiddlewareAddons';
import { INode } from '../../../general/model';
import { CHNode, INodeToCHNode } from '../../../general/node';
import { Interrupt } from '../../../general/scenarioFactory';
import { scenarioFactoryAsync } from '../../../general/scenarioFactoryAsync';
import { ListenerApiLight, lm } from '../../listenerMiddleware';
import { logger } from '../../logger';
import { StoreShape } from '../../models/rootSelector';
import { getNodeId } from '../app/selectors';
import * as fileManagerActions from '../fileManager/actions';
import { getNodes } from '../fileManager/selectors/selectors';
import * as newFolderActions from './actions';
import { Messages } from './model';
import { getName } from './selectors';

type LApiNewFolder = ListenerApiSafe<Pick<StoreShape, 'newFolder'>>;

export function newFolderRootListener() {
  watchNewFolderButtonClicked();
}

async function checkSubmit(lapi: ListenerApiSafe<any>) {
  const action = await _take(
    lapi,
    newFolderActions.confirmed,
    newFolderActions.cancelButtonClicked,
    newFolderActions.modalCloseUiEvent,
  );
  lapi.signal.throwIfAborted();
  if (action.type !== newFolderActions.confirmed.type) throw Interrupt.UserCancelled;
  return;
}

function watchNewFolderButtonClicked() {
  lm.takeLeading(newFolderActions.newFolderButtonClicked, async (_, lapi) => {
    const parentNodeId = _select(lapi, getNodeId);
    if (parentNodeId !== null) {
      const knownNodes = _select(lapi, getNodes);
      const currentAppNodeID = _select(lapi, getNodeId);
      if (!currentAppNodeID) return;
      await newFolderScenario(lapi, parentNodeId, knownNodes, currentAppNodeID);
    }
  });
}

export const createFolderUniqName = scenarioFactoryAsync<
  Pick<StoreShape, 'newFolder'> | StoreShape,
  [string, number, CHNode[], number | null],
  INode
>(
  async (lapi, name, parentNodeID, knownNodes, currentAppNodeID) => {
    async function getListing() {
      if (knownNodes) {
        return knownNodes;
      }
      const r = await api.fs.loadNodes(parentNodeID);
      lapi.signal.throwIfAborted();
      if (!r.success) throw r.reason;
      return r.data.response.nodes.map(INodeToCHNode);
    }

    const ls = await getListing();
    lapi.signal.throwIfAborted();
    let selectedFolderName = name;
    let counter = 0;
    const findNode = (folderName: string) => ls.find((node) => node.name === folderName);
    while (findNode(selectedFolderName)) {
      selectedFolderName = `${name} ${++counter}`;
    }
    const createFolderResult = await api.fs.createFolder(selectedFolderName, parentNodeID);
    lapi.signal.throwIfAborted();
    if (createFolderResult.success) {
      const node = createFolderResult.data.response.node;
      if ('app' in lapi.getState()) {
        logger.lapi(lapi as ListenerApiLight, {
          action: 'Folder',
          sub_action: 'Create',
          // extra: { name: node.name, node_id: node.id },
        });
      }

      if (currentAppNodeID === parentNodeID) {
        lapi.dispatch(fileManagerActions.nodeAdded({ node: INodeToCHNode(node) }));
      }

      return node;
    }

    throw createFolderResult.reason;
  },
  null,
  null,
  'createFolderUniqName',
);

async function scenarioInner(
  lapi: LApiNewFolder,
  parentNodeID: number,
  knownNodes: CHNode[],
  currentAppNodeID: number | null,
): Promise<INode> {
  // open modal
  lapi.dispatch(newFolderActions.modalOpened());

  // wait for OK/cancel
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await checkSubmit(lapi);
    lapi.signal.throwIfAborted();
    // if OK, do the job
    const name = _select(lapi, getName);
    lapi.dispatch(newFolderActions.setInprogress({ inProgress: true }));
    const createFolderResult = await createFolderUniqName(
      lapi,
      name,
      parentNodeID,
      knownNodes,
      currentAppNodeID,
    );
    lapi.signal.throwIfAborted();
    lapi.dispatch(newFolderActions.setInprogress({ inProgress: false }));

    if (createFolderResult.success) {
      return createFolderResult.data;
    }
    lapi.dispatch(Notifications.error(Messages.createFolderError()));
  }
}

function scenarioCleanup(lapi: ListenerApiSafe<any>) {
  lapi.dispatch(newFolderActions.modalClosed());
}
async function scenarioCancel(lapi: ListenerApiSafe<any>) {
  await _take(lapi, newFolderActions.modalCloseUiEvent, newFolderActions.cancelButtonClicked);
}
export const newFolderScenario = scenarioFactoryAsync(
  scenarioInner,
  scenarioCancel,
  scenarioCleanup,
  'New Folder',
);
